import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { SkeletonTable } from '../../../components/common/SkeletonLoader';
import { useDisclosureRegister, type DisclosureRange, type DisclosureRow } from '../../../hooks/usePartnerPortal';
import { formatCentsAud, formatReferralDate, timestampToMillis } from '../../../utils/partnerReferrals';
import { StagePill } from '../ReferralsTable';
import { usePartnerPortalContext } from '../portalContext';

const CARD = 'rounded-xl border border-gray-700 bg-gray-800/40 p-4 sm:p-6';
const INPUT =
  'w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none';
const LABEL = 'block text-sm font-medium text-gray-300 mb-1';

const ISO_DAY = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/**
 * A date input value the register can be asked for. Browsers emit partial
 * years while a date is being typed ("0002-09-01"), so a plausible year is
 * required too; blank means "no bound".
 */
export function isCommittableDay(v: string | undefined): boolean {
  if (!v) return true;
  const m = ISO_DAY.exec(v);
  return !!m && Number(m[1]) >= 2000 && Number(m[1]) <= 2100;
}

/** Both attestations carry a server time. */
export const isFullyDisclosed = (r: Pick<DisclosureRow, 'clientConsentAt' | 'feeDisclosureAt'>): boolean =>
  timestampToMillis(r.clientConsentAt) !== null && timestampToMillis(r.feeDisclosureAt) !== null;

/**
 * The referral disclosure register, viewed in the app only. VALUTTEN data
 * never leaves the app, so this tab has no file or copy action.
 */
const ComplianceTab: React.FC = () => {
  const { hideFees } = usePartnerPortalContext();
  // The inputs edit a draft; only Apply commits it. Each register read is
  // audited server-side (range and row count), so half-typed dates must never
  // trigger a read.
  const [draft, setDraft] = useState<DisclosureRange>({});
  const [applied, setApplied] = useState<DisclosureRange>({});
  const draftInvalid = !isCommittableDay(draft.from) || !isCommittableDay(draft.to);
  const draftInverted = !draftInvalid && !!draft.from && !!draft.to && draft.from > draft.to;
  const draftIsApplied = (draft.from ?? '') === (applied.from ?? '') && (draft.to ?? '') === (applied.to ?? '');
  const canApply = !draftInvalid && !draftInverted && !draftIsApplied;
  const { data, loading, error } = useDisclosureRegister(applied);
  const rows = data?.rows ?? [];
  const disclosed = rows.filter(isFullyDisclosed).length;

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-2`}>
        <h2 className="text-sm font-semibold text-gray-200">Disclosures captured</h2>
        <p className="text-3xl font-semibold text-gray-100">
          {loading || error || rows.length === 0 ? '—' : `${Math.round((disclosed / rows.length) * 100)}%`}
        </p>
        {!loading && !error && <p className="text-sm text-gray-400">{`${disclosed} of ${rows.length} referrals`}</p>}
        <p className="text-xs text-gray-400">
          Every referral records the client's consent and your referral-fee disclosure, each with the time it was
          confirmed.
        </p>
      </div>

      <section className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <h2 className="text-base font-semibold text-gray-100">Disclosure register</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <div>
              <label htmlFor="dr-from" className={LABEL}>Submitted from</label>
              <input
                id="dr-from"
                type="date"
                className={INPUT}
                value={draft.from ?? ''}
                onChange={(e) => setDraft((r) => ({ ...r, from: e.target.value || undefined }))}
              />
            </div>
            <div>
              <label htmlFor="dr-to" className={LABEL}>Submitted to (inclusive)</label>
              <input
                id="dr-to"
                type="date"
                className={INPUT}
                value={draft.to ?? ''}
                onChange={(e) => setDraft((r) => ({ ...r, to: e.target.value || undefined }))}
              />
            </div>
            <button
              type="button"
              onClick={() => setApplied({ ...(draft.from ? { from: draft.from } : {}), ...(draft.to ? { to: draft.to } : {}) })}
              disabled={!canApply}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              Apply dates
            </button>
          </div>
        </div>
        {draftInverted ? (
          <p className="text-xs text-red-300" role="alert">The start date must not be after the end date.</p>
        ) : draftInvalid ? (
          <p className="text-xs text-red-300" role="alert">Enter a complete date.</p>
        ) : (
          <p className="text-xs text-gray-400">Dates are Brisbane calendar days. Leave both blank to see every referral.</p>
        )}

        {data?.truncated && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200" role="alert">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>
              Showing only the 5,000 most recent referrals in this period. Narrow the dates to see the rest.
            </span>
          </div>
        )}

        {loading ? (
          <SkeletonTable rows={3} columns={6} />
        ) : error ? (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300" role="alert">
            Could not load the disclosure register. {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-700 px-6 py-10 text-center text-sm text-gray-400">
            No referrals in this period.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-400">
                <tr>
                  <th className="px-4 py-3 font-medium">Reference</th>
                  <th className="px-4 py-3 font-medium">Submitted</th>
                  <th className="px-4 py-3 font-medium">Client</th>
                  <th className="px-4 py-3 font-medium">Partner</th>
                  <th className="px-4 py-3 font-medium">Broker</th>
                  <th className="px-4 py-3 font-medium">Submitted by</th>
                  <th className="px-4 py-3 font-medium">Consent</th>
                  <th className="px-4 py-3 font-medium">Fee disclosed</th>
                  <th className="px-4 py-3 font-medium">Fee schedule</th>
                  <th className="px-4 py-3 font-medium">Stage</th>
                  {!hideFees && <th className="px-4 py-3 font-medium text-right">Settlement fee</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/70">
                {rows.map((r) => (
                  <tr key={r.referralId}>
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-gray-400">{r.referralId}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-300">{formatReferralDate(r.submittedAt)}</td>
                    <td className="px-4 py-3 text-gray-100">{r.clientName}</td>
                    <td className="px-4 py-3 text-gray-300">{r.partner}</td>
                    <td className="px-4 py-3 text-gray-300">{r.broker ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-300">{r.submittedBy ?? '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-300">
                      {r.clientConsentAt ? formatReferralDate(r.clientConsentAt) : 'Not recorded'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-300">
                      {r.feeDisclosureAt ? formatReferralDate(r.feeDisclosureAt) : 'Not recorded'}
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {typeof r.feeScheduleVersion === 'number' ? `v${r.feeScheduleVersion}` : '—'}
                    </td>
                    <td className="px-4 py-3"><StagePill stage={r.stage} /></td>
                    {!hideFees && (
                      <td className="px-4 py-3 text-right whitespace-nowrap text-gray-100">
                        {typeof r.settlementFeeIncGstCents === 'number' ? formatCentsAud(r.settlementFeeIncGstCents) : '—'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default ComplianceTab;
